import { SendButton } from "@/chat/components/chat/ChatInput";
import { Button, ConfirmDialog } from "@/components/ui/button";
import {
  useAdminSendSessionMessage,
  type AdminSendAs,
  type Session,
} from "@/features/sessions";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  ChatTextIcon,
  PencilSimpleLineIcon,
  ShieldCheckIcon,
  UserIcon,
  XIcon,
  type Icon,
} from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * What a HeyUp administrator can write into a conversation from its history.
 *
 * Folded by default: reviewing a conversation is reading it, and an open text box
 * under every one of them invited typing into a customer's thread by accident.
 * Opened, it asks first *as whom* the text goes, because the three are different
 * acts with different readers:
 *  - an instruction to the agent, which the customer never sees;
 *  - a line as if the customer had typed it (what the box always did);
 *  - an SMS to the customer in the agent's name — the follow-up of a
 *    notification exchange, where nobody is there to chat with.
 *
 * The SMS is the one of the three that leaves HeyUp: it reaches a customer's phone
 * and cannot be taken back, so it is sent only once confirmed — Enter in the box
 * would otherwise text whatever half-sentence was being typed.
 *
 * SYSADMIN only: the caller decides whether to mount it.
 */
const MODES: {
  as: AdminSendAs;
  icon: Icon;
  /** The selected pill and the field's ring share the mode's tone. */
  tone: string;
  ring: string;
}[] = [
  {
    as: "admin",
    icon: ShieldCheckIcon,
    tone: "bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200",
    ring: "border-violet-400/70 dark:border-violet-400/50",
  },
  {
    as: "user",
    icon: UserIcon,
    tone: "bg-primary/10 text-primary",
    ring: "border-primary/50",
  },
  {
    as: "agent_sms",
    icon: ChatTextIcon,
    tone: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200",
    ring: "border-emerald-500/60 dark:border-emerald-400/50",
  },
];

export function AdminComposer({
  session,
  onToggle,
}: {
  session: Session;
  /** Called once the box has folded or unfolded, and the thread above resized. */
  onToggle?: () => void;
}) {
  const { t } = useTranslation("calls");
  const phone = session.context?.phone_number;
  // A notification exchange has nobody to chat with: what is left to do there is
  // text the customer, so that is where the box opens.
  const defaultMode: AdminSendAs =
    session.type === "notifications" && phone ? "agent_sms" : "admin";

  const [open, setOpen] = useState(false);
  const [as, setAs] = useState<AdminSendAs>(defaultMode);
  const [text, setText] = useState("");
  const [confirmingSms, setConfirmingSms] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const send = useAdminSendSessionMessage();

  // Another conversation picked in the list: start folded, on its own default.
  useEffect(() => {
    setOpen(false);
    setAs(defaultMode);
    setText("");
    setConfirmingSms(false);
  }, [session.id, defaultMode]);

  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open, as]);

  // Read through a ref: the host passes a fresh closure on every render, and the
  // box must only report its own folding, not each re-render.
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;
  useEffect(() => {
    onToggleRef.current?.();
  }, [open]);

  if (!open) {
    return (
      <div className="flex shrink-0 items-center justify-between gap-3 border-t px-4 py-2">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheckIcon className="size-3.5" />
          {t("composer.staff_only")}
        </span>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <PencilSimpleLineIcon className="size-4" />
          {t("composer.open")}
        </Button>
      </div>
    );
  }

  const mode = MODES.find((m) => m.as === as) || MODES[0];
  const smsUnavailable = as === "agent_sms" && !phone;
  const canSend = !!text.trim() && !send.isPending && !smsUnavailable;

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSend) return;
    if (as === "agent_sms") setConfirmingSms(true);
    else void deliver();
  };

  const deliver = async () => {
    if (!canSend) return;
    try {
      await send.mutateAsync({ sessionId: session.id, message: text, as });
      setText("");
      if (as === "agent_sms") toast.success(t("composer.sms_sent"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.messageError"),
      );
    }
  };

  return (
    <>
      <form
        onSubmit={submit}
        className="shrink-0 space-y-2 border-t bg-muted/30 px-4 pb-3 pt-2.5"
      >
        <div className="flex items-center gap-2">
          <div
            role="radiogroup"
            aria-label={t("composer.as_label")}
            className="flex min-w-0 flex-wrap gap-1 rounded-lg border bg-background p-0.5"
          >
            {MODES.map((m) => {
              const selected = m.as === as;
              return (
                <button
                  key={m.as}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setAs(m.as)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                    selected
                      ? m.tone
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <m.icon
                    className="size-3.5"
                    weight={selected ? "fill" : "regular"}
                  />
                  {t(`composer.modes.${m.as}.label`)}
                </button>
              );
            })}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto size-7 shrink-0"
            onClick={() => setOpen(false)}
            aria-label={t("composer.close")}
          >
            <XIcon className="size-4" />
          </Button>
        </div>

        <p
          className={cn(
            "text-xs",
            smsUnavailable ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {smsUnavailable
            ? t("composer.modes.agent_sms.unavailable")
            : t(`composer.modes.${as}.hint`, { phone })}
        </p>

        <div
          className={cn(
            "flex items-end gap-2 rounded-xl border bg-background py-1.5 pl-3 pr-1.5 transition-colors",
            mode.ring,
          )}
        >
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={2}
            disabled={smsUnavailable}
            placeholder={t(`composer.modes.${as}.placeholder`)}
            className="max-h-40 min-h-[2.5rem] flex-1 resize-none bg-transparent py-1 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          />
          <SendButton type="submit" disabled={!canSend} />
        </div>
      </form>
      <ConfirmDialog
        open={confirmingSms}
        onOpenChange={setConfirmingSms}
        title={t("composer.modes.agent_sms.confirm_title")}
        description={
          <>
            {t("composer.modes.agent_sms.confirm_description", { phone })}
            <span className="mt-3 block whitespace-pre-wrap rounded-md border bg-muted/50 p-3 text-sm text-foreground">
              {text.trim()}
            </span>
          </>
        }
        confirmLabel={t("composer.modes.agent_sms.confirm")}
        confirmDisabled={!canSend}
        onConfirm={() => void deliver()}
      />
    </>
  );
}
